import {postAdminAdjustment} from "@/lib/adminAdjustment";
import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Share2, Check, X, Pause, Play, Settings, Wallet, PoundSterling, Loader2 } from "lucide-react";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
const pence = (p: number) => `£${(p / 100).toFixed(2)}`;
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

interface Influencer {
  id: number; userId: number; status: string; displayName: string | null; socialHandle: string | null;
  globalCodeId: number | null; code: string | null; appliedAt: string; approvedAt: string | null; userName: string | null; userEmail: string | null;
  referredCount?: number; commissionOrders?: number; lifetimePence?: number;
}
interface Withdrawal {
  id: number; influencerId: number; amountPence: number; status: string; adminNote: string | null;
  requestedAt: string; processedAt: string | null; influencerName: string | null; bank: { accountName?: string; accountNumber?: string; sortCode?: string } | null;
}
interface Config { id: number; discountPercent: number; firstOrderPence: number; secondOrderPence: number; thirdOrderPence: number; subsequentOrderPence: number; clearanceDays: number; minWithdrawalPence: number }
interface Costs { customerDiscountPence: number; commissionPendingPence: number; commissionAvailablePence: number; paidOutPence: number }

export default function AdminInfluencers() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<"influencers" | "withdrawals" | "config">("influencers");
  const [statusFilter, setStatusFilter] = useState("all");
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [config, setConfig] = useState<Config | null>(null);
  const [costs, setCosts] = useState<Costs | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [inf, wd, cfg, cost] = await Promise.all([
      fetch(`/api/admin/influencers?status=${statusFilter}`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : { influencers: [] }),
      fetch(`/api/admin/influencer-withdrawals?status=all`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : { withdrawals: [] }),
      fetch(`/api/admin/influencer-config`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : { config: null }),
      fetch(`/api/admin/influencer-costs`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : null),
    ]);
    setInfluencers(inf.influencers ?? []); setWithdrawals(wd.withdrawals ?? []); setConfig(cfg.config ?? null); setCosts(cost); setLoading(false);
  }, [statusFilter]);
  useEffect(() => { void load(); }, [load]);

  if (isLoading) return null;
  if (!user || user.role !== "admin") return <Redirect to="/admin" />;

  async function act(id: number, path: string, body?: unknown, method = "POST") {
    const res = path==='adjust'?await postAdminAdjustment(`/api/admin/influencers/${id}/${path}`,body as Record<string,unknown>,authHeaders()):await fetch(`/api/admin/influencers/${id}/${path}`, { method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined });
    if (res.ok) { toast({ title: "Done" }); void load(); }
    else { const e = await res.json().catch(() => ({})); toast({ title: e.error || "Failed", variant: "destructive" }); }
  }
  async function processWithdrawal(id: number, status: string) {
    const note = status === "rejected" || status === "failed" ? (prompt("Note for the influencer (optional):") ?? "") : "";
    const res = await fetch(`/api/admin/influencer-withdrawals/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status, adminNote: note }) });
    if (res.ok) { toast({ title: `Withdrawal ${status}` }); void load(); }
    else { const e = await res.json().catch(() => ({})); toast({ title: e.error || "Failed", variant: "destructive" }); }
  }
  async function approve(id: number) {
    const code = prompt("Custom code (leave blank to auto-generate):") ?? "";
    await act(id, "approve", { code: code.trim() || undefined });
  }
  async function reject(id: number) { const reason = prompt("Reason for rejection:") ?? ""; await act(id, "reject", { reason }); }
  async function changeCode(id: number) { const code = prompt("New code (3–20 letters/numbers):"); if (code) await act(id, "code", { code }, "PATCH"); }
  async function adjust(id: number) {
    const amt = prompt("Adjustment amount in pence (negative to deduct):"); if (!amt) return;
    const reason = prompt("Reason (required):"); if (!reason) { toast({ title: "Reason required", variant: "destructive" }); return; }
    await act(id, "adjust", { amountPence: Number(amt), reason });
  }

  return (
    <AdminLayout current="/admin/influencers">
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Share2 size={22} /> Influencers</h1>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {(["influencers", "withdrawals", "config"] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-md text-sm font-semibold capitalize ${tab === t ? "bg-white shadow-sm text-[#1e3a5f]" : "text-slate-500"}`}>{t}</button>
            ))}
          </div>
        </div>

        {costs && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <CostCard label="Customer discount given" value={pence(costs.customerDiscountPence)} />
            <CostCard label="Commission pending" value={pence(costs.commissionPendingPence)} />
            <CostCard label="Commission available" value={pence(costs.commissionAvailablePence)} />
            <CostCard label="Paid out" value={pence(costs.paidOutPence)} />
          </div>
        )}

        {loading && <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>}

        {!loading && tab === "influencers" && (
          <>
            <div className="flex gap-2 flex-wrap">
              {["all", "pending", "active", "paused", "rejected"].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-full text-sm capitalize border ${statusFilter === s ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200"}`}>{s}</button>
              ))}
            </div>
            {influencers.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No influencers.</div>}
            <div className="space-y-2">
              {influencers.map((inf) => (
                <div key={inf.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-semibold text-slate-800">{inf.displayName || inf.userName || `User ${inf.userId}`} <span className="text-xs text-slate-400">{inf.userEmail}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {inf.socialHandle && <span className="mr-2">{inf.socialHandle}</span>}
                      {inf.code && <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{inf.code}</span>}
                      <span className="ml-2 capitalize px-2 py-0.5 rounded-full text-[11px] bg-slate-100">{inf.status}</span>
                      <span className="ml-2">applied {fmtDate(inf.appliedAt)}</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1 font-medium">
                      {(inf.referredCount ?? 0)} referral{(inf.referredCount ?? 0) === 1 ? "" : "s"}
                      <span className="text-slate-400"> · </span>
                      {(inf.commissionOrders ?? 0)} order{(inf.commissionOrders ?? 0) === 1 ? "" : "s"}
                      <span className="text-slate-400"> · </span>
                      <span className="text-emerald-700">£{(((inf.lifetimePence ?? 0)) / 100).toFixed(2)} earned</span>
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {inf.status === "pending" && <>
                      <IconBtn onClick={() => approve(inf.id)} Icon={Check} label="Approve" tone="green" />
                      <IconBtn onClick={() => reject(inf.id)} Icon={X} label="Reject" tone="red" />
                    </>}
                    {inf.status === "active" && <>
                      <IconBtn onClick={() => changeCode(inf.id)} Icon={Settings} label="Code" />
                      <IconBtn onClick={() => act(inf.id, "pause")} Icon={Pause} label="Pause" />
                      <IconBtn onClick={() => adjust(inf.id)} Icon={PoundSterling} label="Adjust" />
                    </>}
                    {inf.status === "paused" && <>
                      <IconBtn onClick={() => act(inf.id, "resume")} Icon={Play} label="Resume" tone="green" />
                      <IconBtn onClick={() => adjust(inf.id)} Icon={PoundSterling} label="Adjust" />
                    </>}
                    {inf.status === "rejected" && <IconBtn onClick={() => approve(inf.id)} Icon={Check} label="Approve" tone="green" />}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && tab === "withdrawals" && (
          <div className="space-y-2">
            {withdrawals.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No withdrawal requests.</div>}
            {withdrawals.map((w) => (
              <div key={w.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold text-slate-800">{w.influencerName || `Influencer ${w.influencerId}`} · {pence(w.amountPence)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="capitalize px-2 py-0.5 rounded-full text-[11px] bg-slate-100">{w.status}</span>
                    <span className="ml-2">{fmtDate(w.requestedAt)}</span>
                    {w.bank && <span className="ml-2 font-mono">{w.bank.accountName} · {w.bank.sortCode} · {w.bank.accountNumber}</span>}
                  </div>
                </div>
                {w.status !== "paid" && (
                  <div className="flex gap-1.5 flex-wrap">
                    {w.status === "pending" && <IconBtn onClick={() => processWithdrawal(w.id, "processing")} Icon={Wallet} label="Processing" />}
                    <IconBtn onClick={() => processWithdrawal(w.id, "paid")} Icon={Check} label="Mark paid" tone="green" />
                    <IconBtn onClick={() => processWithdrawal(w.id, "rejected")} Icon={X} label="Reject" tone="red" />
                    <IconBtn onClick={() => processWithdrawal(w.id, "failed")} Icon={X} label="Failed" tone="red" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "config" && config && <ConfigPanel config={config} onSaved={load} />}
      </div>
    </AdminLayout>
  );
}

function CostCard({ label, value }: { label: string; value: string }) {
  return <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"><div className="text-xs text-slate-400">{label}</div><div className="text-xl font-bold text-[#1e3a5f] mt-1">{value}</div></div>;
}
function IconBtn({ onClick, Icon, label, tone }: { onClick: () => void; Icon: React.ElementType; label: string; tone?: "green" | "red" }) {
  const c = tone === "green" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : tone === "red" ? "text-rose-700 border-rose-200 bg-rose-50" : "text-slate-600 border-slate-200 bg-white";
  return <button onClick={onClick} className={`px-2.5 py-1.5 rounded-lg border text-xs font-semibold flex items-center gap-1 ${c}`}><Icon size={13} /> {label}</button>;
}

function ConfigPanel({ config, onSaved }: { config: Config; onSaved: () => void }) {
  const { toast } = useToast();
  const [f, setF] = useState(config);
  const [busy, setBusy] = useState(false);
  const upd = (k: keyof Config, v: number) => setF((p) => ({ ...p, [k]: v }));
  async function save() {
    setBusy(true);
    const res = await fetch("/api/admin/influencer-config", { method: "PUT", headers: authHeaders(), body: JSON.stringify(f) });
    setBusy(false);
    if (res.ok) { toast({ title: "New config version saved", description: "Existing referrals keep their original terms." }); onSaved(); }
    else toast({ title: "Failed to save", variant: "destructive" });
  }
  const fields: { k: keyof Config; label: string; suffix?: string }[] = [
    { k: "discountPercent", label: "First-order discount %" },
    { k: "firstOrderPence", label: "1st order reward (pence)" },
    { k: "secondOrderPence", label: "2nd order reward (pence)" },
    { k: "thirdOrderPence", label: "3rd order reward (pence)" },
    { k: "subsequentOrderPence", label: "4th+ order reward (pence)" },
    { k: "clearanceDays", label: "Clearance period (days)" },
    { k: "minWithdrawalPence", label: "Minimum withdrawal (pence)" },
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 max-w-lg">
      <h3 className="text-lg font-bold text-slate-800">Programme configuration</h3>
      <p className="text-sm text-slate-500 mt-1">Saving creates a new version. Existing referrals keep the terms they were signed up on — historical earnings are never rewritten.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {fields.map((fl) => (
          <label key={fl.k} className="text-sm text-slate-600">{fl.label}
            <input type="number" value={f[fl.k]} onChange={(e) => upd(fl.k, Math.max(0, Math.trunc(Number(e.target.value) || 0)))} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200" /></label>
        ))}
      </div>
      <button onClick={save} disabled={busy} className="mt-4 bg-[#1e3a5f] text-white font-bold px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Save new version</button>
    </div>
  );
}
